import { create } from 'zustand';
import { themeService } from '../services/theme.service';
import { sindicatoService } from '../services/sindicato.service';
import { applyTheme, THEME_DEFAULTS, applyDarkVarsPublic, removeDarkVarsPublic } from '../lib/theme';

// Extract subdomain slug from current hostname.
// e.g. "equatorial.juritrabalhista.com.br" → "equatorial"
// e.g. "equatorial.localhost" → "equatorial"
// Returns null for localhost, IP addresses, or the apex domain (no subdomain).
function detectSubdomainSlug() {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    const last = parts[parts.length - 1];
    // Support equatorial.localhost (2 parts) in addition to real subdomains (3+ parts)
    const hasSubdomain = parts.length >= 3 || (parts.length === 2 && last === 'localhost');
    if (hasSubdomain && !/^\d+$/.test(parts[0])) {
        return parts[0];
    }
    return null;
}

// Cache keyed by hostname so switching subdomains never bleeds the wrong theme.
const _cacheKey = 'theme_cache:' + window.location.hostname;
const _cached = (() => {
    try {
        const s = localStorage.getItem(_cacheKey);
        return s ? JSON.parse(s) : null;
    } catch {
        return null;
    }
})();

// Apply cached theme synchronously before first render to avoid flash.
if (_cached) applyTheme(_cached);

export const useThemeStore = create((set) => ({
    theme:      _cached ?? THEME_DEFAULTS,
    savedTheme: _cached ?? THEME_DEFAULTS,
    loaded: false,

    loadTheme: async (sindicatoId = null, { skipSlug = false } = {}) => {
        try {
            const slug = skipSlug ? null : detectSubdomainSlug();

            if (sindicatoId) {
                const [globalResult, sindicatoResult] = await Promise.allSettled([
                    themeService.getTheme(),
                    sindicatoService.getViewTheme(sindicatoId),
                ]);
                const final = sindicatoResult.status === 'fulfilled'
                    ? sindicatoResult.value
                    : globalResult.status === 'fulfilled'
                        ? globalResult.value
                        : THEME_DEFAULTS;
                const withMaint = {
                    ...final,
                    maintenance_mode: globalResult.status === 'fulfilled' && !!globalResult.value.maintenance_mode,
                };
                set({ theme: withMaint, savedTheme: withMaint, loaded: true });
                applyTheme(withMaint);
                localStorage.setItem(_cacheKey, JSON.stringify(withMaint));
                return;
            }

            if (slug) {
                const [globalResult, slugResult] = await Promise.allSettled([
                    themeService.getTheme(),
                    sindicatoService.getViewThemeBySlug(slug),
                ]);
                const final = slugResult.status === 'fulfilled'
                    ? slugResult.value
                    : globalResult.status === 'fulfilled'
                        ? globalResult.value
                        : THEME_DEFAULTS;
                const withMaint = {
                    ...final,
                    maintenance_mode: globalResult.status === 'fulfilled' && !!globalResult.value.maintenance_mode,
                };
                set({ theme: withMaint, savedTheme: withMaint, loaded: true });
                applyTheme(withMaint);
                localStorage.setItem(_cacheKey, JSON.stringify(withMaint));
                return;
            }

            const data = await themeService.getTheme();
            set({ theme: data, savedTheme: data, loaded: true });
            applyTheme(data);
            if (!skipSlug) {
                localStorage.setItem(_cacheKey, JSON.stringify(data));
            }
        } catch {
            applyTheme(THEME_DEFAULTS);
            set({ loaded: true });
        }
    },

    previewTheme: (draft) => {
        set({ theme: draft });
        applyTheme(draft);
    },

    toggleDarkMode: () => {
        const isDark = document.documentElement.classList.contains('dark');
        const next = isDark ? 'light' : 'dark';
        document.documentElement.classList.toggle('dark', !isDark);
        document.documentElement.dataset.theme = next;
        localStorage.setItem('theme', next);
        if (!isDark) applyDarkVarsPublic();
        else removeDarkVarsPublic();
    },

    saveTheme: async (id, updates) => {
        const updated = await themeService.updateTheme(id, updates);
        set({ theme: updated, savedTheme: updated });
        applyTheme(updated);
        localStorage.setItem(_cacheKey, JSON.stringify(updated));
        return updated;
    },
}));
